import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";

const CTA_PHONE_DISPLAY = "(11) 92004-3024";
const CTA_PHONE_WA = "5511920043024";

/**
 * FloatingSidebarAd — anúncio 300×600 (half-page IAB) flutuante FIXO no lado
 * direito da viewport em telas ≥ 1280px. Some completamente em mobile/tablet
 * para não competir com o conteúdo. Renderiza somente se houver um anúncio
 * ativo no slot "sidebar" — sem casa-ad, o componente retorna null.
 *
 * Ao clicar no X, o anúncio é substituído por um painel de "Anunciar aqui" com
 * 3 opções, igual aos demais slots. "Fechar permanentemente" grava localStorage
 * e o anúncio some totalmente por 30 dias.
 */
export default function FloatingSidebarAd() {
  const [ad, setAd] = useState(null);
  const [closed, setClosed] = useState(false); // clicou no X → mostra painel
  const [dismissedForever, setDismissedForever] = useState(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    try {
      const v = window.localStorage.getItem("orion_ad_dismiss_sidebar");
      if (v) {
        const ts = parseInt(v, 10);
        if (Number.isFinite(ts) && Date.now() - ts < 30 * 24 * 60 * 60 * 1000) {
          setDismissedForever(true);
        } else {
          window.localStorage.removeItem("orion_ad_dismiss_sidebar");
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/ads/serve/sidebar")
      .then((r) => {
        if (cancelled) return;
        setAd(r.data?.ad || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (ad?.id && !viewedRef.current) {
      viewedRef.current = true;
      api.post(`/ads/${ad.id}/view`).catch(() => {});
    }
  }, [ad?.id]);

  if (!ad || dismissedForever) return null;

  const dismissForever = () => {
    try {
      window.localStorage.setItem("orion_ad_dismiss_sidebar", String(Date.now()));
    } catch {}
    setDismissedForever(true);
  };

  // Painel de opções após o usuário clicar no X — replica o padrão dos demais slots.
  if (closed) {
    return (
      <aside
        className="hidden xl:block fixed right-5 top-1/2 -translate-y-1/2 z-30"
        style={{ width: 300 }}
        aria-label="Painel de anúncio fechado"
        data-testid="floating-sidebar-ad-closed"
      >
        <div className="bg-[#F8F5EE] border border-[#DFD8C7] shadow-2xl p-5 text-center">
          <div className="text-[10px] uppercase tracking-[0.28em] text-orion-orange font-semibold mb-2">
            Conteúdo Verificado e Patrocinado
          </div>
          <div className="text-sm text-orion-purple leading-snug mb-4">
            Este anúncio ajuda a manter o Viagens Orion gratuito.
          </div>
          <div className="flex flex-col items-stretch gap-2">
            <a
              href={`https://wa.me/${CTA_PHONE_WA}?text=${encodeURIComponent("Olá! Quero anunciar no Viagens Orion da Viagens Orion.")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1 bg-orion-orange text-white px-4 py-2 text-[11px] uppercase tracking-widest font-bold hover:bg-[#E04828] transition-colors"
              data-testid="floating-sidebar-ad-anuncie"
            >
              Anunciar aqui · {CTA_PHONE_DISPLAY}
            </a>
            <button
              type="button"
              onClick={() => setClosed(false)}
              className="inline-flex items-center justify-center gap-1 border border-orion-purple text-orion-purple px-4 py-2 text-[11px] uppercase tracking-widest font-semibold hover:bg-orion-purple hover:text-white transition-colors"
              data-testid="floating-sidebar-ad-restore"
            >
              Voltar ao anúncio
            </button>
            <button
              type="button"
              onClick={dismissForever}
              className="inline-flex items-center justify-center gap-1 text-[11px] uppercase tracking-widest text-[#8A8191] hover:text-orion-purple underline underline-offset-2 px-4 py-2"
              data-testid="floating-sidebar-ad-dismiss-forever"
              title="Não mostrar este anúncio por 30 dias"
            >
              Fechar permanentemente
            </button>
          </div>
        </div>
      </aside>
    );
  }

  // Only render on XL+ screens. Tailwind hidden classes drop it on smaller.
  return (
    <aside
      className="hidden xl:block fixed right-5 top-1/2 -translate-y-1/2 z-30 pointer-events-none"
      style={{ width: 300 }}
      aria-label="Publicidade lateral"
      data-testid="floating-sidebar-ad"
    >
      <div
        className="relative pointer-events-auto bg-white shadow-2xl border border-black/10"
        style={{ width: 300 }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setClosed(true);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Fechar este anúncio"
          title="Fechar este anúncio"
          className="absolute top-1 right-1 z-30 w-7 h-7 rounded-full bg-black/80 hover:bg-black text-white flex items-center justify-center shadow-lg border-2 border-white cursor-pointer transition-colors"
          data-testid="floating-sidebar-ad-close"
        >
          <X size={14} strokeWidth={2.5} />
        </button>

        <AdLink ad={ad}>
          <img
            src={ad.imagem_desktop}
            alt={ad.titulo || "Anúncio"}
            className="block w-full h-auto object-contain"
            style={{ maxWidth: 300, maxHeight: 600 }}
            loading="lazy"
          />
        </AdLink>

        <span className="absolute bottom-1 right-1 bg-black/50 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded pointer-events-none">
          Publi
        </span>
      </div>
    </aside>
  );
}

function AdLink({ ad, children }) {
  const url = ad?.slug ? `/api/go/${ad.slug}` : ad?.link || "#";
  const trackClick = () => {
    if (ad?.id) api.post(`/ads/${ad.id}/click`).catch(() => {});
  };
  return (
    <a
      href={url}
      target="_blank"
      rel="sponsored noopener nofollow"
      onClick={trackClick}
      className="block"
      data-testid="floating-sidebar-ad-link"
    >
      {children}
    </a>
  );
}
